import {
  ASSET_ICON,
  CARREIRAS_MARCAS_PRINCIPAIS,
  CARREIRAS_MARCAS_PRINCIPAIS_SET,
  FALLBACK_EMPRESAS,
  LOGO_FALLBACKS,
  LOGOS_LIGHT_PLATE,
} from './constants'
import type {
  BrandInfo,
  EmpresaGrupo,
  HashRoute,
  VagaResumo,
} from './types'

export function resolveAssetUrl(url?: string | null): string {
  if (!url) return ''
  const s = String(url).trim()
  if (/^https?:\/\//i.test(s) || s.startsWith('data:') || s.startsWith('/')) return s
  // paths relative like assets/... → absolute public path
  return `/${s.replace(/^\.\//, '')}`
}

export function logoForSlug(slug?: string | null, logoUrl?: string | null): string {
  const resolved = resolveAssetUrl(logoUrl)
  if (resolved) return resolved
  const key = String(slug || '').toLowerCase()
  return LOGO_FALLBACKS[key] || ASSET_ICON
}

export function logoPlateClass(slug?: string | null): string {
  const key = String(slug || '').toLowerCase()
  return LOGOS_LIGHT_PLATE.has(key) ? 'cr-logo-plate--light' : 'cr-logo-plate--dark'
}

export function normalizeEmpresaGrupo(raw: Partial<EmpresaGrupo> | null | undefined): EmpresaGrupo | null {
  if (!raw || typeof raw !== 'object') return null
  const slug = String(raw.slug || '').trim().toLowerCase()
  if (!slug) return null
  const fb = FALLBACK_EMPRESAS.find((e) => e.slug === slug) || ({} as Partial<EmpresaGrupo>)
  return {
    slug,
    nome: raw.nome || fb.nome || slug,
    nome_curto: raw.nome_curto || fb.nome_curto || raw.nome || slug,
    tagline: raw.tagline || fb.tagline || '',
    sobre: raw.sobre || fb.sobre || '',
    site_url: raw.site_url || fb.site_url || '',
    logo_url: logoForSlug(slug, raw.logo_url || fb.logo_url),
    cor_primaria: raw.cor_primaria || fb.cor_primaria || '#000070',
    cor_secundaria: raw.cor_secundaria || fb.cor_secundaria || '#0018c8',
    areas: Array.isArray(raw.areas) && raw.areas.length ? raw.areas : (fb.areas || []),
    total_vagas: Number(raw.total_vagas) || 0,
    eh_holding: !!(raw.eh_holding ?? fb.eh_holding),
    destaque: !!(raw.destaque ?? fb.destaque),
    parent_slug: raw.parent_slug != null ? raw.parent_slug : (fb.parent_slug ?? null),
    ordem: Number(raw.ordem ?? fb.ordem ?? 0),
  }
}

export function sortEmpresas(list: EmpresaGrupo[]): EmpresaGrupo[] {
  return [...list].sort(
    (a, b) => (a.ordem || 0) - (b.ordem || 0) || String(a.nome).localeCompare(String(b.nome)),
  )
}

/** Filtra marcas principais do portal Carreiras (chips + árvore). */
export function empresasPrincipais(
  list: EmpresaGrupo[],
  opts: { incluirHolding?: boolean } = {},
): EmpresaGrupo[] {
  const { incluirHolding = false } = opts
  const ordem = new Map<string, number>(
    CARREIRAS_MARCAS_PRINCIPAIS.map((slug, i) => [slug, i]),
  )
  return list
    .filter((e) => {
      if (e.eh_holding) return incluirHolding
      return CARREIRAS_MARCAS_PRINCIPAIS_SET.has(String(e.slug))
    })
    .sort((a, b) => {
      if (a.eh_holding) return -1
      if (b.eh_holding) return 1
      return (ordem.get(String(a.slug)) ?? 99) - (ordem.get(String(b.slug)) ?? 99)
    })
}

export function localStr(v: { cidade?: string; uf?: string }): string {
  const c = v.cidade || ''
  const u = v.uf || ''
  if (c && u) return `${c}, ${u}`
  return c || u || 'Brasil'
}

export function statusBadgeClass(status?: string): string {
  const map: Record<string, string> = {
    em_andamento: 'andamento',
    proposta: 'proposta',
    contratado: 'contratado',
    reprovado: 'reprovado',
    desistiu: 'desistiu',
  }
  return map[status || ''] || 'andamento'
}

export function formatInscrito(iso?: string): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return ''
  }
}

export function brandFromVaga(
  v: VagaResumo | null | undefined,
  empresasGrupo: EmpresaGrupo[],
  empresaFiltro: string,
): BrandInfo {
  const find = (slug: string) => empresasGrupo.find((e) => e.slug === slug) || null
  const emp = v?.empresa
  if (emp && (emp.slug || emp.nome)) {
    const slug = String(emp.slug || '').toLowerCase()
    const fromGrupo = find(slug)
    return {
      slug: slug || 'grupo-hi',
      nome: emp.nome || fromGrupo?.nome || 'Grupo HI',
      logo_url: logoForSlug(slug || 'grupo-hi', emp.logo_url || fromGrupo?.logo_url),
      cor_primaria: emp.cor_primaria || fromGrupo?.cor_primaria || '#000070',
      cor_secundaria: emp.cor_secundaria || fromGrupo?.cor_secundaria || '#0018c8',
      site_url: emp.site_url || fromGrupo?.site_url || '',
    }
  }
  if (empresaFiltro) {
    const f = find(empresaFiltro)
    if (f) {
      return {
        slug: f.slug,
        nome: f.nome,
        logo_url: f.logo_url,
        cor_primaria: f.cor_primaria,
        cor_secundaria: f.cor_secundaria,
        site_url: f.site_url,
      }
    }
  }
  return {
    slug: 'grupo-hi',
    nome: 'Grupo HI',
    logo_url: '/assets/empresas/grupo-hi.png',
    cor_primaria: '#000070',
    cor_secundaria: '#00d931',
    site_url: '',
  }
}

export function parseHash(hash = typeof window !== 'undefined' ? window.location.hash : ''): HashRoute {
  const h = hash || ''
  const auth = h.match(/^#\/auth-ok(?:\?(.*))?$/i)
  if (auth) {
    const params = new URLSearchParams(auth[1] || '')
    return {
      type: 'auth-ok',
      token: params.get('token') || '',
      vaga: params.get('vaga') || '',
      erro: params.get('erro') || '',
    }
  }
  if (/^#\/minhas\/?$/i.test(h)) return { type: 'minhas' }
  if (/^#\/sobre\/?$/i.test(h)) return { type: 'sobre' }
  const emp = h.match(/^#\/empresa\/([a-z0-9_-]+)\/?$/i)
  if (emp) return { type: 'empresa', slug: emp[1].toLowerCase() }
  const m = h.match(/#\/vaga\/([0-9a-f-]{36})/i)
  if (m) return { type: 'vaga', id: m[1] }
  return { type: 'home' }
}

export function setHash(hash: string) {
  const next = hash === '#/' ? `${window.location.pathname}${window.location.search}` : hash
  if (hash === '#/') {
    if (window.location.hash) history.replaceState(null, '', next)
  } else if (window.location.hash !== hash) {
    history.replaceState(null, '', hash)
  }
}

export function proseLines(texto?: string): string[] {
  if (!texto) return []
  return String(texto)
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => l.replace(/^[•\-*]\s*/, ''))
}

export function onImgError(fallback: string) {
  return (e: { currentTarget: HTMLImageElement }) => {
    const img = e.currentTarget
    if (img.dataset.fallbackApplied === '1') return
    img.dataset.fallbackApplied = '1'
    img.src = fallback
  }
}
